/**
 * PFM Page Component
 *
 * Two-step flow:
 * 1. User fills a form (reference_id, mobile, PAN, name)
 * 2. On submit, calls FPAS backend which calls PFM SDK Init API to get session_token
 * 3. Launches PFM journey in a device preview iframe using the token
 */

import React, { useState, useCallback } from 'react';
import { Smartphone, Monitor, Tablet, Loader2, ArrowLeft, RefreshCw, Shuffle } from 'lucide-react';
import { InputField, Button } from 'equal-ds-ui';
import axios from 'axios';
import { pfmAPI } from '../../../services/pfm-api.service';
import type { PFMFormValues, PFMFormErrors, PFMSDKInitResponse } from '../../../types/pfm';

// Read PFM config from env.js (window.__env.pfm)
const getEnv = () => (window as any).__env?.pfm || {};
const getPfmJourneyBaseUrl = () => getEnv().journeyBaseUrl || 'https://uat.pfm.equal.in';

// Validation patterns
const MOBILE_REGEX = /^[6-9]\d{9}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

// Device presets
const DEVICE_PRESETS = [
  { id: 'iphone-se', name: 'iPhone SE', width: 375, height: 667, icon: Smartphone },
  { id: 'iphone-14', name: 'iPhone 14', width: 390, height: 844, icon: Smartphone },
  { id: 'iphone-pro-max', name: 'iPhone Pro Max', width: 430, height: 932, icon: Smartphone },
  { id: 'android', name: 'Android', width: 412, height: 915, icon: Smartphone },
  { id: 'ipad', name: 'iPad', width: 768, height: 1024, icon: Tablet },
  { id: 'desktop', name: 'Desktop', width: 1280, height: 800, icon: Monitor },
];

const DEFAULT_FORM: PFMFormValues = {
  reference_id: '',
  mobile_number: '',
  pan: '',
  name: '',
};

function validateForm(values: PFMFormValues): PFMFormErrors {
  const errors: PFMFormErrors = {};

  if (!values.reference_id.trim()) {
    errors.reference_id = 'Reference ID is required';
  }
  if (!values.mobile_number.trim()) {
    errors.mobile_number = 'Mobile number is required';
  } else if (!MOBILE_REGEX.test(values.mobile_number)) {
    errors.mobile_number = 'Enter a valid 10-digit mobile number';
  }
  if (!values.pan.trim()) {
    errors.pan = 'PAN is required';
  } else if (!PAN_REGEX.test(values.pan.toUpperCase())) {
    errors.pan = 'Enter a valid PAN (e.g., ABCDE1234F)';
  }

  return errors;
}

const isPhoneOrTablet = (deviceId: string) => deviceId !== 'desktop';

export const PFMPage: React.FC = () => {
  // Form state
  const [form, setForm] = useState<PFMFormValues>(DEFAULT_FORM);
  const [errors, setErrors] = useState<PFMFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Journey state
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState('desktop');
  const [isIframeLoading, setIsIframeLoading] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  const currentDevice = DEVICE_PRESETS.find(d => d.id === selectedDevice) || DEVICE_PRESETS[1];
  const showDeviceFrame = isPhoneOrTablet(selectedDevice);
  const showJourney = !!sessionToken;

  // PFM journey URL — matches SDK: /pfm?access_token=...
  const pfmJourneyUrl = sessionToken
    ? `${getPfmJourneyBaseUrl()}/pfm?access_token=${sessionToken}`
    : '';

  const handleChange = useCallback((field: keyof PFMFormValues, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
    setSubmitError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const response = await pfmAPI.initSDK({
        reference_id: form.reference_id.trim(),
        user_profile: {
          mobile_number: form.mobile_number.trim(),
          pan: form.pan.trim().toUpperCase(),
          ...(form.name.trim() && { name: form.name.trim() }),
        },
      });

      // Backend wraps PFM API response inside { status, message, data: { ... } }
      const pfmData = (response as any).data || response;
      if (pfmData.status !== 'SUCCESS' || !pfmData.session_token) {
        setSubmitError(pfmData.message || (response as any).message || 'Failed to initialize PFM session');
        return;
      }

      setSessionToken(pfmData.session_token);
      setIsIframeLoading(true);
    } catch (err: unknown) {
      console.error('[PFMPage] SDK init failed:', err);
      const message = axios.isAxiosError(err)
        ? (err.response?.data as PFMSDKInitResponse)?.message ?? err.message
        : err instanceof Error
          ? err.message
          : 'Failed to initialize PFM. Please try again.';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [form]);

  const handleBackToForm = () => {
    setSessionToken(null);
  };

  const handleRefreshJourney = () => {
    setIsIframeLoading(true);
    setIframeKey(prev => prev + 1);
  };

  const isFormValid = Object.keys(validateForm(form)).length === 0;

  const screenStyle: React.CSSProperties = { width: currentDevice.width, height: currentDevice.height };

  // ── Journey View ──
  if (showJourney) {
    return (
      <div className="equal-ds-root flex flex-col bg-gray-100 h-full">
        {/* Journey Header */}
        <div className="bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0 z-10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToForm}
                className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to form"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-base font-semibold text-text-primary">PFM Journey</h1>
                <p className="text-xs text-text-secondary">
                  {currentDevice.name} — {currentDevice.width} × {currentDevice.height}
                </p>
              </div>
            </div>

            {/* Device Selector */}
            <div className="flex items-center gap-1 flex-wrap">
              {DEVICE_PRESETS.map((device) => {
                const Icon = device.icon;
                const isSelected = selectedDevice === device.id;
                return (
                  <button
                    key={device.id}
                    onClick={() => setSelectedDevice(device.id)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                      isSelected
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-transparent'
                    }`}
                    title={`${device.name} (${device.width} × ${device.height})`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden lg:inline">{device.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <button
                onClick={handleRefreshJourney}
                className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${isIframeLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Device Preview Area */}
        <div className="flex-1 min-h-0 flex items-start justify-center overflow-auto p-4">
          <div
            className={`transition-all duration-300 flex-shrink-0 ${
              showDeviceFrame ? 'bg-gray-900 rounded-[3rem] p-4 shadow-2xl' : ''
            }`}
            style={showDeviceFrame ? { width: currentDevice.width + 32, maxHeight: '100%' } : undefined}
          >
            {showDeviceFrame && (
              <div className="flex justify-center mb-2">
                <div className="w-32 h-6 bg-gray-900 rounded-b-2xl flex items-center justify-center">
                  <div className="w-16 h-4 bg-black rounded-full" />
                </div>
              </div>
            )}
            <div
              className={`relative bg-white overflow-hidden ${showDeviceFrame ? 'rounded-2xl' : ''}`}
              style={screenStyle}
            >
              {isIframeLoading && (
                <div className="absolute inset-0 bg-white flex items-center justify-center z-10">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                    <p className="text-sm text-text-secondary">Loading PFM...</p>
                  </div>
                </div>
              )}
              <iframe
                key={iframeKey}
                src={pfmJourneyUrl}
                title="PFM Journey"
                className="h-full border-0"
                style={{ width: 'calc(100% + 20px)' }}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                onLoad={() => setIsIframeLoading(false)}
              />
            </div>
            {showDeviceFrame && (
              <div className="flex justify-center mt-2">
                <div className="w-32 h-1 bg-gray-600 rounded-full" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Form View ──
  return (
    <div className="equal-ds-root h-full">
      <h1 className="text-2xl font-semibold text-text-primary tracking-wide">PFM - Personal Finance Management</h1>
      <p className="text-sm text-text-secondary mt-1">Enter user details to launch the PFM journey</p>

      <div className="flex justify-center mt-6">
        <div className="max-w-md w-full border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
          <div className="grid grid-cols-1 gap-5">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <InputField
                  label="Reference ID"
                  required
                  value={form.reference_id}
                  onChange={(e) => handleChange('reference_id', e.target.value)}
                  error={errors.reference_id}
                  placeholder="Unique user identifier"
                  disabled={isSubmitting}
                />
              </div>
              <Button
                variant="secondary"
                size="md"
                onClick={() => handleChange('reference_id', `equal-${Math.random().toString(36).substring(2, 8)}`)}
                disabled={isSubmitting}
                title="Auto-generate"
              >
                <Shuffle className="w-4 h-4" />
              </Button>
            </div>
            <InputField
              label="Mobile Number"
              required
              value={form.mobile_number}
              onChange={(e) => handleChange('mobile_number', e.target.value)}
              error={errors.mobile_number}
              placeholder="10-digit mobile number"
              maxLength={10}
              disabled={isSubmitting}
            />
            <InputField
              label="PAN"
              required
              value={form.pan}
              onChange={(e) => handleChange('pan', e.target.value.toUpperCase())}
              error={errors.pan}
              placeholder="ABCDE1234F"
              maxLength={10}
              disabled={isSubmitting}
            />
            <InputField
              label="Name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={errors.name}
              placeholder="Full name (optional)"
              disabled={isSubmitting}
            />
          </div>

          {submitError && (
            <div className="mt-5 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}

          <div className="mt-5">
            <Button
              variant="primary"
              size="md"
              onClick={handleSubmit}
              disabled={isSubmitting || !isFormValid}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Initializing PFM...
                </>
              ) : (
                <>
                  <Smartphone className="w-4 h-4 mr-2" />
                  Launch PFM Journey
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PFMPage;
